import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { projectApi } from '../hooks/useApi';
import { C, Card, HelpBox, DataTable, Td, Badge, Tag, Empty, RiskItem } from '../components/UI';
import { G_LBL, G_COLOR, V_LBL, V_COLOR, riskLabel, riskColor, ETAT_COLOR, PERTINENCE_COLOR, STATUT_COLOR } from '../components/ebios';

export default function SharedPage() {
  const { token } = useParams();
  const [p, setP]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    projectApi.getShared(token)
      .then(data => setP(data))
      .catch(() => setError('Lien invalide ou expiré.'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <Center>Chargement...</Center>;
  if (error)   return <Center style={{ color: C.danger }}>{error}</Center>;
  if (!p)      return null;

  const sorted = [...(p.so||[])].sort((a,b)=>(b.gravite*b.vrais)-(a.gravite*a.vrais));

  return (
    <div style={{ minHeight:'100vh', background:C.bg, color:C.text, fontFamily:"'Segoe UI',system-ui,sans-serif" }}>
      {/* Header */}
      <header style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, padding:'16px 32px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontFamily:'Georgia,serif', fontSize:20 }}>EBIOS RM — Vue partagée</div>
          <div style={{ fontSize:12, color:C.text3, marginTop:3 }}>Lecture seule</div>
        </div>
        <div style={{ background:'rgba(79,124,255,.1)', color:C.accent, border:'1px solid rgba(79,124,255,.2)', padding:'6px 14px', borderRadius:20, fontSize:12, fontWeight:600 }}>🔒 Lecture seule</div>
      </header>

      <main style={{ maxWidth:1100, margin:'0 auto', padding:'32px 28px' }}>
        {/* Titre projet */}
        <div style={{ marginBottom:32 }}>
          <h1 style={{ fontFamily:'Georgia,serif', fontSize:32, margin:'0 0 8px' }}>{p.name}</h1>
          <div style={{ fontSize:14, color:C.text2 }}>{p.org} {p.date && `— ${p.date}`}</div>
        </div>

        {/* Contexte */}
        <Card title="Contexte">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
            <div>
              <div style={{ fontSize:11, color:C.text3, textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Périmètre</div>
              <div style={{ fontSize:14, color:C.text2, lineHeight:1.6 }}>{p.scope||'—'}</div>
            </div>
            <div>
              <div style={{ fontSize:11, color:C.text3, textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Contexte</div>
              <div style={{ fontSize:14, color:C.text2, lineHeight:1.6 }}>{p.context||'—'}</div>
            </div>
          </div>
        </Card>

        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:24 }}>
          {[['📋',p.er?.length||0,'Événements redoutés',C.accent],
            ['🕵️',p.sr?.length||0,'Sources de risque','#7c5cff'],
            ['🗺️',p.ss?.length||0,'Scénarios stratégiques',C.warn],
            ['⚠️',(p.so||[]).filter(s=>s.gravite*s.vrais>=6).length,'Risques élevés/critiques',C.danger],
          ].map(([icon,val,lbl,col]) => (
            <div key={lbl} style={{ background:C.surface, border:`1px solid ${C.border}`, borderTop:`2px solid ${col}`, borderRadius:12, padding:20 }}>
              <div style={{ fontSize:24, marginBottom:8 }}>{icon}</div>
              <div style={{ fontFamily:'Georgia,serif', fontSize:32, lineHeight:1 }}>{val}</div>
              <div style={{ fontSize:11, color:C.text3, marginTop:6, textTransform:'uppercase', letterSpacing:1 }}>{lbl}</div>
            </div>
          ))}
        </div>

        {/* Synthèse risques */}
        <Card title="Synthèse des risques" subtitle="Scénarios classés par niveau">
          {sorted.length === 0
            ? <Empty icon="📊" text="Aucun scénario opérationnel" />
            : sorted.map(s => {
                const sc = s.gravite*s.vrais;
                const ss = (p.ss||[]).find(x=>x.id===s.ssId);
                return <RiskItem key={s.id}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:600, color:C.text }}>{s.nom}</div>
                    <div style={{ fontSize:12, color:C.text3, marginTop:3 }}>{ss?.nom}</div>
                  </div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    <Badge color={G_COLOR[s.gravite]}>{G_LBL[s.gravite]}</Badge>
                    <Badge color={V_COLOR[s.vrais]}>{V_LBL[s.vrais]}</Badge>
                    <Badge color={riskColor(sc)}>Score {sc} — {riskLabel(sc)}</Badge>
                  </div>
                </RiskItem>;
              })
          }
        </Card>

        {/* ER */}
        <Card title={`Événements redoutés (${p.er?.length||0})`}>
          <DataTable cols={['Valeur métier','Événement redouté','Impacts','Gravité']} empty={<Empty icon="🎯" text="Aucun" />}>
            {(p.er||[]).map(e => <tr key={e.id}>
              <Td bold>{e.vm}</Td><Td>{e.name}</Td><Td small>{e.impacts}</Td>
              <Td><Badge color={G_COLOR[e.gravite]}>{G_LBL[e.gravite]}</Badge></Td>
            </tr>)}
          </DataTable>
        </Card>

        {/* Sources de risque */}
        <Card title={`Sources de risque (${p.sr?.length||0})`}>
          <DataTable cols={['Source','Objectif visé','Motivation','Pertinence']} empty={<Empty icon="🕵️" text="Aucune" />}>
            {(p.sr||[]).map(s => <tr key={s.id}>
              <Td bold>{s.type}{s.nom&&<><br/><small style={{color:C.text3}}>{s.nom}</small></>}</Td>
              <Td>{s.ov}</Td><Td>{s.motiv}</Td>
              <Td><Badge color={PERTINENCE_COLOR[s.pert]}>{s.pert}</Badge></Td>
            </tr>)}
          </DataTable>
        </Card>

        {/* Scénarios stratégiques */}
        <Card title={`Scénarios stratégiques (${p.ss?.length||0})`}>
          <DataTable cols={['Scénario','ER liés','Chemin d\'attaque','Gravité']} empty={<Empty icon="🗺️" text="Aucun" />}>
            {(p.ss||[]).map(s => {
              const erTags = (s.erIds||[]).map(eid=>{ const e=(p.er||[]).find(x=>x.id===eid); return e?<Tag key={eid}>{e.name}</Tag>:null; });
              return <tr key={s.id}>
                <Td bold>{s.nom}</Td>
                <Td>{erTags.length?erTags:'—'}</Td>
                <Td small>{s.chemin}</Td>
                <Td><Badge color={G_COLOR[s.gravite]}>{G_LBL[s.gravite]}</Badge></Td>
              </tr>;
            })}
          </DataTable>
        </Card>

        {/* Plan de traitement */}
        <Card title={`Plan de traitement (${p.mesures?.length||0})`}>
          <DataTable cols={['Mesure','Stratégie','Responsable','Coût','Échéance','Statut']} empty={<Empty icon="🔐" text="Aucune mesure" />}>
            {(p.mesures||[]).map(m => <tr key={m.id}>
              <Td bold>{m.nom}</Td><Td>{m.strat}</Td>
              <Td>{m.resp||'—'}</Td><Td>{m.cout}</Td><Td>{m.ech||'—'}</Td>
              <Td><Badge color={STATUT_COLOR[m.stat]}>{m.stat}</Badge></Td>
            </tr>)}
          </DataTable>
        </Card>

        <div style={{ textAlign:'center', fontSize:12, color:C.text3, padding:'20px 0' }}>
          EBIOS RM Manager — Vue partagée en lecture seule
        </div>
      </main>
    </div>
  );
}

function Center({ children, style: s }) {
  return <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center', color:C.text2, fontSize:16, ...s }}>{children}</div>;
}
